import type { Ticket as ManageTicket } from '../../types/ticket.types';
import {
  getEventGroupKey,
  getGroupingStatusKey,
} from '../ManageTickets/manageTicketGrouping';
import { BARCODE_STATUS_OPTIONS, getAutomaticBarcodeStatus } from '../../utils/noBarcodeRules';

type UserDetailsBarcodeStatus = (typeof BARCODE_STATUS_OPTIONS)[number]['value'];

export function getUserDetailsBarcodeStatus(ticket: {
  barcodeStatus?: unknown;
  event?: unknown;
  row?: unknown;
  barcodeAttached?: boolean;
  barcode?: unknown;
} | null | undefined): UserDetailsBarcodeStatus {
  const computedStatus = String(ticket?.barcodeStatus ?? '').trim();
  if (BARCODE_STATUS_OPTIONS.some((option) => option.value === computedStatus)) {
    return computedStatus as UserDetailsBarcodeStatus;
  }

  const automaticStatus = getAutomaticBarcodeStatus(ticket?.event, ticket?.row, undefined);
  if (automaticStatus) return automaticStatus;

  const hasBarcode =
    ticket?.barcodeAttached === true || Boolean(String(ticket?.barcode ?? '').trim());
  return hasBarcode ? 'Barcode Available' : 'Barcode Pending';
}

export type UserDetailsPricingMethod = 'MANUAL' | 'FOLLOW_MARKET';

/** Explicit UI selection including Nothing (clears Manual / Follow the Market). */
export type PricingMethodSelection = UserDetailsPricingMethod | 'NONE' | '';

export const normalizePricingMethod = (
  value: unknown,
): UserDetailsPricingMethod | null => {
  const normalized = String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, '');

  if (normalized === 'MANUAL' || normalized === 'MANUALLYPRICED') return 'MANUAL';
  if (normalized === 'FOLLOWMARKET' || normalized === 'FOLLOWTHEMARKET') {
    return 'FOLLOW_MARKET';
  }
  // NONE / NOTHING / empty → no icon
  return null;
};

export const pricingMethodMatches = (
  value: unknown,
  filter: 'all' | UserDetailsPricingMethod | 'NONE',
): boolean => {
  if (filter === 'all') return true;
  if (filter === 'NONE') return normalizePricingMethod(value) == null;
  return normalizePricingMethod(value) === filter;
};

export const resolvePricingMethodSelection = (
  value: unknown,
): PricingMethodSelection => {
  const normalized = String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, '');
  if (normalized === 'NONE' || normalized === 'NOTHING') return 'NONE';
  return normalizePricingMethod(value) ?? 'NONE';
};

/** API payload: MANUAL / FOLLOW_MARKET / NONE (null in DB). */
export const toPricingMethodApiValue = (
  selection: PricingMethodSelection,
): 'MANUAL' | 'FOLLOW_MARKET' | 'NONE' | undefined => {
  if (selection === 'MANUAL' || selection === 'FOLLOW_MARKET') return selection;
  if (selection === 'NONE' || selection === '') return 'NONE';
  return undefined;
};

export type UserDetailsTicketGroupingIdentity = {
  tpId: string;
  tId?: string | null;
  vlListingId?: string | null;
  eventId?: string | null;
  event?: string | null;
  date?: string | null;
  section?: string | null;
  row?: string | null;
  statusKey: string;
  paymentStatus?: string | null;
  payoutStatus?: string | null;
};

export type UserTicketGroupingSource = {
  tpId: string;
  id?: string | null;
  vlListingId?: string | null;
  eventId?: string | null;
  event?: string | null;
  section?: string | null;
  row?: string | null;
  seatNo?: string | number | null;
  seatMode?: 'odd' | 'even' | null;
  rawStatus?: string | null;
  status?: string | null;
  paymentStatus?: string | null;
  payoutStatus?: string | null;
};

/** Shape a user-details seat the way Manage Tickets grouping reads it. */
export const toManageTicketGroupingRecord = (
  ticket: UserTicketGroupingSource,
): ManageTicket => ({
  tp_id: ticket.tpId,
  t_id: ticket.id || '',
  tp_vl_listing_id: ticket.vlListingId || '',
  e_id: ticket.eventId || '',
  e_name: ticket.event || '',
  tp_section: ticket.section || '',
  tp_row: ticket.row || '',
  tp_seat_no: ticket.seatNo ?? null,
  tp_seat_mode: ticket.seatMode ?? null,
  tp_status: ticket.rawStatus || ticket.status || '',
  tp_payment_status: ticket.paymentStatus ?? null,
  tp_payout_status: ticket.payoutStatus ?? null,
} as unknown as ManageTicket);

const identityToManageTicket = (
  ticket: UserDetailsTicketGroupingIdentity,
): ManageTicket => toManageTicketGroupingRecord({
  tpId: ticket.tpId,
  id: ticket.tId,
  vlListingId: ticket.vlListingId,
  eventId: ticket.eventId,
  event: ticket.event,
  section: ticket.section,
  row: ticket.row,
  rawStatus: ticket.statusKey,
  paymentStatus: ticket.paymentStatus,
  payoutStatus: ticket.payoutStatus,
});

export const getUserDetailsTicketGroupingBaseKey = (
  ticket: UserDetailsTicketGroupingIdentity,
): string => getEventGroupKey(identityToManageTicket(ticket)) || `ticket-${ticket.tpId}`;

export const getUserDetailsTicketGroupKey = (
  ticket: UserDetailsTicketGroupingIdentity,
): string => {
  const manageTicket = identityToManageTicket(ticket);
  const baseKey = getEventGroupKey(manageTicket);
  if (!baseKey) return `ticket-${ticket.tpId}`;
  return JSON.stringify([baseKey, getGroupingStatusKey(manageTicket)]);
};
